// Pin Jobs tab component - manage Pinterest pins for reposting

import { useState, useCallback, useEffect } from "react";
import styles from "./PinJobsTab.module.css";
import { apiUrl } from "../config";

interface PinJob {
  id: number;
  original_pin_id: string;
  original_title: string | null;
  original_description: string | null;
  original_image_url: string;
  original_image_url_large: string | null;
  original_pinner_username: string | null;
  original_pinner_name: string | null;
  original_board_name: string | null;
  original_link: string | null;
  original_domain: string | null;
  is_video: number;
  video_url: string | null;
  tags: string;
  rewritten_title: string | null;
  rewritten_description: string | null;
  custom_link: string | null;
  site_id: string | null;
  metricool_account_id: number | null;
  target_board: string | null;
  schedule_date: string | null;
  status: string;
  pinterest_scheduled: number;
  pinterest_scheduled_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface Site {
  id: string;
  name: string;
  domain: string;
  pinterest_brand_name: string;
  pinterest_boards: string;
}

interface MetricoolAccount {
  id: number;
  name: string;
  pinterest_brand_name: string;
  pinterest_boards: string[];
  custom_link: string;
  is_default: number;
}

interface PinJobsTabProps {
  disabled?: boolean;
}

export function PinJobsTab({ disabled }: PinJobsTabProps) {
  const [pinJobs, setPinJobs] = useState<PinJob[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [metricoolAccounts, setMetricoolAccounts] = useState<MetricoolAccount[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingJob, setEditingJob] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{
    rewritten_title: string;
    rewritten_description: string;
    custom_link: string;
    site_id: string;
    metricool_account_id: string;
    target_board: string;
    schedule_date: string;
  }>({
    rewritten_title: "",
    rewritten_description: "",
    custom_link: "",
    site_id: "",
    metricool_account_id: "",
    target_board: "",
    schedule_date: "",
  });
  const [bulkAction, setBulkAction] = useState<string>("");
  const [bulkSiteId, setBulkSiteId] = useState<string>("");
  const [bulkMetricoolAccountId, setBulkMetricoolAccountId] = useState<string>("");
  const [bulkCustomLink, setBulkCustomLink] = useState<string>("");
  const [bulkScheduleDate, setBulkScheduleDate] = useState<string>("");
  const [bulkScheduleDateEnd, setBulkScheduleDateEnd] = useState<string>("");
  const [useDateRange, setUseDateRange] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("pending");

  // Load pin jobs and sites
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [jobsRes, sitesRes, metricoolRes] = await Promise.all([
        fetch(apiUrl(`/api/pin-jobs${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`)),
        fetch(apiUrl("/api/sites")),
        fetch(apiUrl("/api/metricool/accounts")),
      ]);

      if (jobsRes.ok) {
        const jobsText = await jobsRes.text();
        if (jobsText) {
          const jobsData = JSON.parse(jobsText);
          setPinJobs(jobsData.pinJobs || []);
        }
      }

      if (sitesRes.ok) {
        const sitesText = await sitesRes.text();
        if (sitesText) {
          const sitesData = JSON.parse(sitesText);
          setSites(sitesData.sites || []);
        }
      }

      if (metricoolRes.ok) {
        const metricoolText = await metricoolRes.text();
        if (metricoolText) {
          const metricoolData = JSON.parse(metricoolText);
          setMetricoolAccounts(metricoolData.accounts || []);
        }
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleSelect = (id: number) => {
    setSelectedJobs((prev) =>
      prev.includes(id) ? prev.filter((j) => j !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === pinJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(pinJobs.map((j) => j.id));
    }
  };

  const handleSelectCount = (count: number) => {
    if (count <= 0) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(pinJobs.slice(0, count).map((j) => j.id));
    }
  };

  const handleEdit = (job: PinJob) => {
    setEditingJob(job.id);
    setEditForm({
      rewritten_title: job.rewritten_title || job.original_title || "",
      rewritten_description: job.rewritten_description || job.original_description || "",
      custom_link: job.custom_link || "",
      site_id: job.site_id || "",
      metricool_account_id: job.metricool_account_id ? String(job.metricool_account_id) : "",
      target_board: job.target_board || "",
      schedule_date: job.schedule_date || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;

    try {
      // Build payload - only send site_id OR metricool_account_id
      const payload: Record<string, unknown> = {
        rewritten_title: editForm.rewritten_title,
        rewritten_description: editForm.rewritten_description,
        custom_link: editForm.custom_link,
        target_board: editForm.target_board,
        schedule_date: editForm.schedule_date,
      };

      if (editForm.metricool_account_id) {
        payload.metricool_account_id = parseInt(editForm.metricool_account_id);
        payload.site_id = null;
      } else if (editForm.site_id) {
        payload.site_id = editForm.site_id;
        payload.metricool_account_id = null;
      }

      const response = await fetch(apiUrl(`/api/pin-jobs/${editingJob}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await loadData();
        setEditingJob(null);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to save");
      }
    } catch (err) {
      setError("Failed to save changes");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this pin job?")) return;

    try {
      const response = await fetch(apiUrl(`/api/pin-jobs/${id}`), { method: "DELETE" });
      if (response.ok) {
        await loadData();
        setSelectedJobs((prev) => prev.filter((j) => j !== id));
      }
    } catch (err) {
      setError("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedJobs.length} pin jobs?`)) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), { method: "DELETE" })
        )
      );
      await loadData();
      setSelectedJobs([]);
    } catch (err) {
      setError("Failed to delete some jobs");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRewrite = async (id: number) => {
    setIsProcessing(true);
    try {
      const response = await fetch(apiUrl(`/api/pin-jobs/${id}/rewrite`), {
        method: "POST",
      });
      const data = await response.json();
      if (data.success) {
        await loadData();
      } else {
        setError(data.error || "Failed to rewrite");
      }
    } catch (err) {
      setError("Failed to rewrite");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkRewrite = async () => {
    if (selectedJobs.length === 0) return;

    // Only rewrite jobs that haven't been rewritten yet
    const jobsToRewrite = selectedJobs.filter((id) => {
      const job = pinJobs.find((j) => j.id === id);
      return job && !job.rewritten_title;
    });

    if (jobsToRewrite.length === 0) return;

    setIsProcessing(true);
    setError(null);
    try {
      const response = await fetch(apiUrl("/api/pin-jobs/bulk-rewrite"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinJobIds: jobsToRewrite }),
      });
      const data = await response.json();
      await loadData();
      const success = data.results?.filter((r: { success: boolean }) => r.success).length || 0;
      if (success < jobsToRewrite.length) {
        setError(`Rewrote ${success}/${jobsToRewrite.length} jobs`);
      }
    } catch (err) {
      setError("Failed to rewrite jobs");
    } finally {
      setIsProcessing(false);
    }
  };

  // Count how many selected jobs need rewriting
  const selectedNeedRewrite = selectedJobs.filter((id) => {
    const job = pinJobs.find((j) => j.id === id);
    return job && !job.rewritten_title;
  }).length;

  const handleSchedule = async (id: number) => {
    setIsProcessing(true);
    try {
      const response = await fetch(apiUrl(`/api/pin-jobs/${id}/schedule`), {
        method: "POST",
      });
      const data = await response.json();
      if (data.success) {
        await loadData();
      } else {
        setError(data.error || "Failed to schedule");
      }
    } catch (err) {
      setError("Failed to schedule");
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper to generate dates distributed across a range
  const getDistributedDates = (startDate: string, endDate: string, count: number): string[] => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const dates: string[] = [];
    for (let i = 0; i < count; i++) {
      // Distribute evenly across the date range
      const dayOffset = Math.floor((i * totalDays) / count);
      const date = new Date(start);
      date.setDate(date.getDate() + dayOffset);
      dates.push(date.toISOString().split('T')[0] as string);
    }
    return dates;
  };

  const handleBulkSchedule = async () => {
    if (selectedJobs.length === 0) return;

    setIsProcessing(true);
    setError(null);
    try {
      // Build schedule data - include dates if provided
      let scheduleDates: Record<number, string> | undefined;

      if (useDateRange && bulkScheduleDate && bulkScheduleDateEnd) {
        // Distribute dates across range
        const distributedDates = getDistributedDates(bulkScheduleDate, bulkScheduleDateEnd, selectedJobs.length);
        scheduleDates = {};
        selectedJobs.forEach((id, index) => {
          scheduleDates![id] = distributedDates[index] ?? bulkScheduleDate;
        });
      } else if (bulkScheduleDate) {
        // Single date for all
        scheduleDates = {};
        selectedJobs.forEach((id) => {
          scheduleDates![id] = bulkScheduleDate;
        });
      }

      // Send all data to bulk-schedule API (handles dates + scheduling)
      const response = await fetch(apiUrl("/api/pin-jobs/bulk-schedule"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pinJobIds: selectedJobs,
          scheduleDates: scheduleDates,
        }),
      });
      const data = await response.json();
      await loadData();
      const success = data.results?.filter((r: { success: boolean }) => r.success).length || 0;
      if (success < selectedJobs.length) {
        setError(`Scheduled ${success}/${selectedJobs.length} jobs`);
      }
      setSelectedJobs([]);
      setBulkScheduleDate("");
      setBulkScheduleDateEnd("");
      setUseDateRange(false);
    } catch (err) {
      setError("Failed to schedule jobs");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkUpdateSite = async () => {
    if (selectedJobs.length === 0 || !bulkSiteId) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ site_id: bulkSiteId, metricool_account_id: null }),
          })
        )
      );
      await loadData();
    } catch (err) {
      setError("Failed to update site");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkUpdateMetricoolAccount = async () => {
    if (selectedJobs.length === 0 || !bulkMetricoolAccountId) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ metricool_account_id: parseInt(bulkMetricoolAccountId), site_id: null }),
          })
        )
      );
      await loadData();
    } catch (err) {
      setError("Failed to update metricool account");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkUpdateLink = async () => {
    if (selectedJobs.length === 0 || !bulkCustomLink) return;

    setIsProcessing(true);
    try {
      await Promise.all(
        selectedJobs.map((id) =>
          fetch(apiUrl(`/api/pin-jobs/${id}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ custom_link: bulkCustomLink }),
          })
        )
      );
      await loadData();
    } catch (err) {
      setError("Failed to update link");
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return styles.statusScheduled;
      case "error":
        return styles.statusError;
      case "pending":
      default:
        return styles.statusPending;
    }
  };

  const getSiteBoards = (siteId: string): string[] => {
    const site = sites.find((s) => s.id === siteId);
    if (!site) return [];
    try {
      return JSON.parse(site.pinterest_boards || "[]");
    } catch {
      return [];
    }
  };

  const getMetricoolAccountBoards = (accountId: string): string[] => {
    const account = metricoolAccounts.find((a) => a.id === parseInt(accountId));
    return account?.pinterest_boards || [];
  };

  const getActiveBoards = (): string[] => {
    if (editForm.metricool_account_id) {
      return getMetricoolAccountBoards(editForm.metricool_account_id);
    }
    if (editForm.site_id) {
      return getSiteBoards(editForm.site_id);
    }
    return [];
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Pin Jobs</h2>
        <div className={styles.headerActions}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="scheduled">Scheduled</option>
            <option value="error">Error</option>
          </select>
          <button onClick={loadData} className={styles.refreshBtn} disabled={isLoading}>
            {isLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={() => setError(null)} className={styles.dismissBtn}>×</button>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedJobs.length > 0 && (
        <div className={styles.bulkActions}>
          <span className={styles.selectedCount}>
            {selectedJobs.length} selected
          </span>
          <div className={styles.bulkButtons}>
            <button
              onClick={handleBulkRewrite}
              className={styles.bulkBtn}
              disabled={isProcessing || selectedNeedRewrite === 0}
              title={selectedNeedRewrite === 0 ? "All selected pins already rewritten" : `Rewrite ${selectedNeedRewrite} pins`}
            >
              Rewrite{selectedNeedRewrite > 0 ? ` (${selectedNeedRewrite})` : ""}
            </button>
            <select
              value={bulkSiteId}
              onChange={(e) => setBulkSiteId(e.target.value)}
              className={styles.bulkSelect}
            >
              <option value="">Select Site...</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleBulkUpdateSite}
              className={styles.bulkBtn}
              disabled={isProcessing || !bulkSiteId}
            >
              Set Site
            </button>
            <select
              value={bulkMetricoolAccountId}
              onChange={(e) => {
                const accountId = e.target.value;
                setBulkMetricoolAccountId(accountId);
                // Auto-fill bulk custom link from account's default link
                const account = metricoolAccounts.find((a) => a.id === parseInt(accountId));
                if (account?.custom_link) {
                  setBulkCustomLink(account.custom_link);
                }
              }}
              className={styles.bulkSelect}
            >
              <option value="">Metricool Account...</option>
              {metricoolAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleBulkUpdateMetricoolAccount}
              className={styles.bulkBtn}
              disabled={isProcessing || !bulkMetricoolAccountId}
            >
              Set Account
            </button>
            <input
              type="text"
              value={bulkCustomLink}
              onChange={(e) => setBulkCustomLink(e.target.value)}
              placeholder="Custom link..."
              className={styles.bulkInput}
            />
            <button
              onClick={handleBulkUpdateLink}
              className={styles.bulkBtn}
              disabled={isProcessing || !bulkCustomLink}
            >
              Set Link
            </button>
            <label className={styles.dateRangeToggle} title="Split posts across date range">
              <input
                type="checkbox"
                checked={useDateRange}
                onChange={(e) => setUseDateRange(e.target.checked)}
              />
              Range
            </label>
            <input
              type="date"
              value={bulkScheduleDate}
              onChange={(e) => setBulkScheduleDate(e.target.value)}
              className={styles.bulkInput}
              title={useDateRange ? "Start date" : "Schedule date (leave empty for next day)"}
              placeholder="From"
            />
            {useDateRange && (
              <>
                <span className={styles.dateRangeSeparator}>to</span>
                <input
                  type="date"
                  value={bulkScheduleDateEnd}
                  onChange={(e) => setBulkScheduleDateEnd(e.target.value)}
                  className={styles.bulkInput}
                  title="End date"
                  min={bulkScheduleDate}
                />
              </>
            )}
            <button
              onClick={handleBulkSchedule}
              className={styles.bulkBtnPrimary}
              disabled={isProcessing || (useDateRange && (!bulkScheduleDate || !bulkScheduleDateEnd))}
              title={useDateRange && bulkScheduleDate && bulkScheduleDateEnd ? `Split ${selectedJobs.length} posts across date range` : "Schedule all selected"}
            >
              Schedule All
            </button>
            <button
              onClick={handleBulkDelete}
              className={styles.bulkBtnDanger}
              disabled={isProcessing}
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Pin Jobs List */}
      {isLoading && pinJobs.length === 0 ? (
        <div className={styles.loading}>Loading...</div>
      ) : pinJobs.length === 0 ? (
        <div className={styles.empty}>
          No pin jobs yet. Search Pinterest and click "Add to Pin Jobs" to get started.
        </div>
      ) : (
        <div className={styles.jobsList}>
          <div className={styles.listHeader}>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={selectedJobs.length === pinJobs.length && pinJobs.length > 0}
                onChange={handleSelectAll}
              />
              Select All
            </label>
            <div className={styles.selectCountWrapper}>
              <input
                type="number"
                min="0"
                max={pinJobs.length}
                placeholder="Select #"
                className={styles.selectCountInput}
                onChange={(e) => handleSelectCount(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          {pinJobs.map((job) => (
            <div
              key={job.id}
              className={`${styles.jobCard} ${selectedJobs.includes(job.id) ? styles.selected : ""}`}
            >
              <div className={styles.jobCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedJobs.includes(job.id)}
                  onChange={() => handleToggleSelect(job.id)}
                />
              </div>

              <div className={styles.jobImage}>
                <img
                  src={job.original_image_url}
                  alt={job.original_title || "Pin"}
                  loading="lazy"
                />
                {job.is_video === 1 && <span className={styles.videoBadge}>Video</span>}
              </div>

              <div className={styles.jobContent}>
                {editingJob === job.id ? (
                  <div className={styles.editForm}>
                    <input
                      type="text"
                      value={editForm.rewritten_title}
                      onChange={(e) =>
                        setEditForm({ ...editForm, rewritten_title: e.target.value })
                      }
                      placeholder="Rewritten title"
                      className={styles.editInput}
                    />
                    <textarea
                      value={editForm.rewritten_description}
                      onChange={(e) =>
                        setEditForm({ ...editForm, rewritten_description: e.target.value })
                      }
                      placeholder="Rewritten description"
                      className={styles.editTextarea}
                      rows={3}
                    />
                    <input
                      type="text"
                      value={editForm.custom_link}
                      onChange={(e) =>
                        setEditForm({ ...editForm, custom_link: e.target.value })
                      }
                      placeholder="Custom link (required)"
                      className={styles.editInput}
                    />
                    <div className={styles.editRow}>
                      <select
                        value={editForm.site_id}
                        onChange={(e) =>
                          setEditForm({ ...editForm, site_id: e.target.value, metricool_account_id: "", target_board: "" })
                        }
                        className={styles.editSelect}
                      >
                        <option value="">Select Site...</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                      <span className={styles.orText}>or</span>
                      <select
                        value={editForm.metricool_account_id}
                        onChange={(e) => {
                          const accountId = e.target.value;
                          const account = metricoolAccounts.find((a) => a.id === parseInt(accountId));
                          setEditForm({
                            ...editForm,
                            metricool_account_id: accountId,
                            site_id: "",
                            target_board: "",
                            custom_link: account?.custom_link || editForm.custom_link,
                          });
                        }}
                        className={styles.editSelect}
                      >
                        <option value="">Metricool Account...</option>
                        {metricoolAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.editRow}>
                      <select
                        value={editForm.target_board}
                        onChange={(e) =>
                          setEditForm({ ...editForm, target_board: e.target.value })
                        }
                        className={styles.editSelect}
                        disabled={!editForm.site_id && !editForm.metricool_account_id}
                      >
                        <option value="">Auto-detect board</option>
                        {getActiveBoards().map((board) => (
                          <option key={board} value={board}>
                            {board}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="date"
                      value={editForm.schedule_date}
                      onChange={(e) =>
                        setEditForm({ ...editForm, schedule_date: e.target.value })
                      }
                      className={styles.editInput}
                    />
                    <div className={styles.editActions}>
                      <button onClick={handleSaveEdit} className={styles.saveBtn}>
                        Save
                      </button>
                      <button
                        onClick={() => setEditingJob(null)}
                        className={styles.cancelBtn}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.jobTitle}>
                      {job.rewritten_title || job.original_title || "Untitled"}
                      {job.rewritten_title && (
                        <span className={styles.rewrittenBadge}>Rewritten</span>
                      )}
                    </div>
                    <div className={styles.jobDescription}>
                      {(job.rewritten_description || job.original_description || "").slice(0, 150)}
                      {(job.rewritten_description || job.original_description || "").length > 150 ? "..." : ""}
                    </div>
                    <div className={styles.jobMeta}>
                      {job.custom_link && (
                        <span className={styles.metaItem}>
                          🔗 {job.custom_link.slice(0, 40)}...
                        </span>
                      )}
                      {job.site_id && (
                        <span className={styles.metaItem}>
                          📍 {sites.find((s) => s.id === job.site_id)?.name || job.site_id}
                        </span>
                      )}
                      {job.metricool_account_id && (
                        <span className={styles.metaItem}>
                          📊 {metricoolAccounts.find((a) => a.id === job.metricool_account_id)?.name || `Account #${job.metricool_account_id}`}
                        </span>
                      )}
                      {job.target_board && (
                        <span className={styles.metaItem}>📌 {job.target_board}</span>
                      )}
                    </div>
                    <div className={styles.jobFooter}>
                      <span className={`${styles.status} ${getStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                      {job.pinterest_scheduled_at && (
                        <span className={styles.scheduledAt}>
                          Scheduled: {new Date(job.pinterest_scheduled_at).toLocaleString()}
                        </span>
                      )}
                      {job.error_message && (
                        <span className={styles.errorMsg}>{job.error_message}</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className={styles.jobActions}>
                <button
                  onClick={() => handleEdit(job)}
                  className={styles.actionBtn}
                  disabled={editingJob === job.id}
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleRewrite(job.id)}
                  className={styles.actionBtn}
                  disabled={isProcessing}
                  title="AI Rewrite"
                >
                  🤖
                </button>
                <button
                  onClick={() => handleSchedule(job.id)}
                  className={styles.actionBtn}
                  disabled={isProcessing || !job.rewritten_title || !job.custom_link || (!job.site_id && !job.metricool_account_id)}
                  title="Schedule"
                >
                  📤
                </button>
                <button
                  onClick={() => handleDelete(job.id)}
                  className={styles.actionBtnDanger}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
